import type { default as DB, MetainfoMVCC } from '../../../db/DB';
import type { StripeClient } from '../../../server/Stripe';
import { deepEquals } from '../../../utils/ObjUtils';
import { duration } from '../../../utils/Utils';

export function tryParseApplicationFeePercent(stripeAccountId: string): number {
    const rawValue = process.env.STRIPE_APPLICATION_FEE_PERCENT;
    if (!rawValue) {
        throw Error(
            'STRIPE_APPLICATION_FEE_PERCENT env var must be configured when making subscriptions with a connected account',
        );
    }

    try {
        const applicationFeePercents = JSON.parse(rawValue);
        if (!applicationFeePercents || typeof applicationFeePercents !== 'object') {
            throw Error('Expected a JSON object');
        }

        const accountValue = applicationFeePercents[stripeAccountId] || applicationFeePercents.default;
        if (accountValue === undefined) {
            throw Error('Missing property "default"');
        }

        const isValidNumber =
            typeof accountValue === 'number' &&
            accountValue >= 0 &&
            accountValue <= 100 &&
            decimalPlaces(accountValue) <= 2;

        if (!isValidNumber) {
            throw Error('Object values must be decimal numbers between 0 and 100 with at most two decimal places');
        }

        return accountValue;
    } catch (error: any) {
        throw Error(`Invalid STRIPE_APPLICATION_FEE_PERCENT env var value ${rawValue}: ${error.message}`);
    }
}

function decimalPlaces(n: number): number {
    return n.toString().includes('.') ? n.toString().split('.').pop()!.length : 0;
}

export async function updatePaymentSubscriptionStatusIfExpired(opts: {
    dateNow: () => number;
    db: DB<any>;
    metainfoMVCC: MetainfoMVCC;
    reportError: (error: Error) => Promise<void>;
    stripe: StripeClient;
    userId: string;
}): Promise<MetainfoMVCC['paymentSubscription'] | void> {
    const { dateNow, db, metainfoMVCC, reportError, stripe, userId } = opts;

    const paymentSubscription = metainfoMVCC.paymentSubscription;
    if (!(paymentSubscription && paymentSubscriptionStatusIsExpired(paymentSubscription, dateNow()))) {
        return;
    }

    try {
        const subscription = await stripe.getSubscription({
            stripeAccountId: paymentSubscription.stripeAccountId,
            subscriptionId: paymentSubscription.status.id,
        });

        const newStatus: MetainfoMVCC['paymentSubscription'] = {
            lastUpdated: dateNow(),
            status: stripe.subscriptionToStatus(subscription),
            stripeAccountId: paymentSubscription.stripeAccountId,
        };

        if (deepEquals(newStatus.status, paymentSubscription.status)) {
            return;
        }

        await db.metainfoMVCC.setPaymentSubscriptionStatus(userId, newStatus);

        return newStatus;
    } catch (error: any) {
        error.message = `Stripe subscription failed to refresh: ${error.message}`;
        await reportError(error);
    }
}

function paymentSubscriptionStatusIsExpired(paymentSubscription: { lastUpdated?: number }, now: number): boolean {
    return !paymentSubscription.lastUpdated || paymentSubscription.lastUpdated + duration({ hours: 12 }) < now;
}
